import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Layers, X } from 'lucide-react';
import { Checkbox } from '@ooh/ui/components/checkbox';
import { Label } from '@ooh/ui/components/label';
import { Button } from '@ooh/ui/components/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ooh/ui/components/tooltip';
import { cn } from '@ooh/ui/lib/utils';
import { poiQueries } from '../api/queries';
import { getCategoryLabel } from '../utils/category-label';

interface PoiLayerTogglePanelProps {
  visibleCategories: Set<string>;
  onVisibleCategoriesChange: (visibleCategories: Set<string>) => void;
  /** Transport layer integration — optional, renders transport section at bottom of panel */
  transportPublicEnabled?: boolean;
  onTransportPublicToggle?: () => void;
  roadsEnabled?: boolean;
  onRoadsToggle?: () => void;
  /** Show zoom hint under public transport toggle when zoom is below city threshold */
  showTransportZoomHint?: boolean;
  /** Show zoom hint under roads toggle when zoom is below road threshold */
  showRoadsZoomHint?: boolean;
  /** Show loading spinner in transport section header while bbox fetch is in-flight */
  isTransportLoading?: boolean;
  /** Show empty hint in transport section when layer is on but response has no features */
  isTransportEmpty?: boolean;
  /** Administrative-area layer integration — optional, renders its own section at the bottom of the panel */
  adminAreaEnabled?: boolean;
  onAdminAreaToggle?: () => void;
  /** Show loading spinner next to the administrative-area toggle while bbox fetch is in-flight */
  isAdminAreaLoading?: boolean;
  /** Show a recoverable error hint when the administrative-area geometry request failed */
  isAdminAreaError?: boolean;
}

// eslint-disable-next-line complexity -- Multiple conditional render branches (POI categories + transport section + zoom hints)
export function PoiLayerTogglePanel({
  visibleCategories,
  onVisibleCategoriesChange,
  transportPublicEnabled,
  onTransportPublicToggle,
  roadsEnabled,
  onRoadsToggle,
  showTransportZoomHint = false,
  showRoadsZoomHint = false,
  isTransportLoading = false,
  isTransportEmpty = false,
  adminAreaEnabled,
  onAdminAreaToggle,
  isAdminAreaLoading = false,
  isAdminAreaError = false,
}: PoiLayerTogglePanelProps) {
  const { auth } = useRouteContext({ from: '__root__' });
  const { t } = useTranslation('map');

  const [open, setOpen] = React.useState(false);

  const { data: categoriesData } = useQuery(poiQueries.categories(auth));
  const categories = React.useMemo(
    () => (Array.isArray(categoriesData) ? categoriesData : []),
    [categoriesData],
  );

  const allKeys = React.useMemo(
    () => new Set(categories.map((c) => `${c.osm_key}-${c.osm_value}`)),
    [categories],
  );

  const allSelected = allKeys.size > 0 && visibleCategories.size === allKeys.size;
  const someSelected = visibleCategories.size > 0 && visibleCategories.size < allKeys.size;

  const handleMasterToggle = () => {
    if (allSelected) {
      onVisibleCategoriesChange(new Set());
    } else {
      onVisibleCategoriesChange(new Set(allKeys));
    }
  };

  const handleCategoryToggle = (key: string, checked: boolean) => {
    const next = new Set(visibleCategories);
    if (checked) next.add(key);
    else next.delete(key);
    onVisibleCategoriesChange(next);
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                'relative shadow-sm rounded-md',
                someSelected && 'ring-2 ring-primary/40',
              )}
            >
              <Button
                type="button"
                variant="secondary"
                size="icon-sm"
                onClick={() => setOpen((v) => !v)}
                data-testid="poi-layer-toggle-trigger"
                aria-label={t('pois.layerPanel.showPois')}
              >
                <Layers className="h-4 w-4" strokeWidth={1.8} />
              </Button>
              {someSelected ? (
                <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary pointer-events-none" />
              ) : null}
            </div>
          </TooltipTrigger>
          <TooltipContent side="left">{t('pois.layerPanel.showPois')}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {open ? (
        <div
          className="bg-background border border-border rounded-lg shadow-md w-52 overflow-hidden"
          data-testid="poi-layer-toggle-panel"
        >
          <div className="flex items-center justify-between px-3 py-2 border-b border-border">
            <div className="flex items-center gap-2">
              <Checkbox
                id="poi-master-toggle"
                checked={allSelected ? true : someSelected ? 'indeterminate' : false}
                onCheckedChange={handleMasterToggle}
                data-testid="poi-master-toggle"
              />
              <Label htmlFor="poi-master-toggle" className="text-[13px] font-medium cursor-pointer">
                {t('pois.layerPanel.showPois')}
              </Label>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              onClick={() => setOpen(false)}
              aria-label={t('pois.layerPanel.close')}
            >
              <X className="h-3.5 w-3.5" strokeWidth={2} />
            </Button>
          </div>

          <div className="max-h-64 overflow-y-auto py-1.5 px-3 space-y-1.5">
            {categories.map((cat) => {
              const key = `${cat.osm_key}-${cat.osm_value}`;
              const isVisible = visibleCategories.has(key);
              const checkboxId = `poi-cat-${key}`;
              return (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox
                    id={checkboxId}
                    checked={isVisible}
                    onCheckedChange={(v) => handleCategoryToggle(key, v === true)}
                    data-testid={`poi-cat-toggle-${key}`}
                  />
                  <Label
                    htmlFor={checkboxId}
                    className="text-[12px] cursor-pointer text-muted-foreground leading-tight"
                  >
                    {getCategoryLabel(cat, t)}
                  </Label>
                </div>
              );
            })}
          </div>

          {onTransportPublicToggle != null && onRoadsToggle != null ? (
            <div className="space-y-2 border-t border-border px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider block">
                  {t('transportLayers.controls.sectionLabel')}
                </span>
                {isTransportLoading ? (
                  <span
                    className="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground border-t-transparent animate-spin"
                    data-testid="transport-layer-loading-spinner"
                  />
                ) : null}
              </div>
              <div className="space-y-0.5">
                <div className="flex items-center justify-between gap-2">
                  <Label
                    htmlFor="transport-public-toggle"
                    className="text-[12px] cursor-pointer text-muted-foreground leading-tight"
                  >
                    {t('transportLayers.controls.publicTransportLabel')}
                  </Label>
                  <Checkbox
                    id="transport-public-toggle"
                    checked={transportPublicEnabled === true}
                    onCheckedChange={() => onTransportPublicToggle()}
                    data-testid="transport-toggle-public"
                  />
                </div>
                {showTransportZoomHint ? (
                  <p
                    className="text-[11px] text-amber-600 dark:text-amber-400 leading-tight"
                    data-testid="transport-public-zoom-hint"
                  >
                    {t('transportLayers.controls.zoomHintPublic')}
                  </p>
                ) : null}
              </div>
              <div className="space-y-0.5">
                <div className="flex items-center justify-between gap-2">
                  <Label
                    htmlFor="transport-roads-toggle"
                    className="text-[12px] cursor-pointer text-muted-foreground leading-tight"
                  >
                    {t('transportLayers.controls.roadsLabel')}
                  </Label>
                  <Checkbox
                    id="transport-roads-toggle"
                    checked={roadsEnabled === true}
                    onCheckedChange={() => onRoadsToggle()}
                    data-testid="transport-toggle-roads"
                  />
                </div>
                {showRoadsZoomHint ? (
                  <p
                    className="text-[11px] text-amber-600 dark:text-amber-400 leading-tight"
                    data-testid="transport-roads-zoom-hint"
                  >
                    {t('transportLayers.controls.zoomHintRoads')}
                  </p>
                ) : null}
              </div>
              {isTransportEmpty ? (
                <p
                  className="text-[11px] text-muted-foreground pt-1 leading-tight"
                  data-testid="transport-empty-hint"
                >
                  {t('transportLayers.controls.emptyLabel')}
                </p>
              ) : null}
            </div>
          ) : null}

          {onAdminAreaToggle != null ? (
            <div className="space-y-0.5 border-t border-border px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <Label
                  htmlFor="admin-area-toggle"
                  className="text-[12px] cursor-pointer text-muted-foreground leading-tight"
                >
                  {t('administrativeAreaLayer.controls.toggleLabel')}
                </Label>
                <div className="flex items-center gap-1.5">
                  {isAdminAreaLoading ? (
                    <span
                      className="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground border-t-transparent animate-spin"
                      data-testid="admin-area-loading-spinner"
                    />
                  ) : null}
                  <Checkbox
                    id="admin-area-toggle"
                    checked={adminAreaEnabled === true}
                    onCheckedChange={() => onAdminAreaToggle()}
                    data-testid="admin-area-toggle"
                  />
                </div>
              </div>
              {isAdminAreaError ? (
                <p
                  className="text-[11px] text-amber-600 dark:text-amber-400 leading-tight"
                  data-testid="admin-area-error-hint"
                >
                  {t('administrativeAreaLayer.controls.errorLabel')}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
